import React, { useState, useEffect } from 'react';
import { Search, Filter, Upload, Download, Clock, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import DocumentLibrary from './components/DocumentLibrary';
import DocumentViewer from './components/DocumentViewer';
import EvidenceCollection from './components/EvidenceCollection';
import RenewalTracking from './components/RenewalTracking';
import AuditTrail from './components/AuditTrail';
import ExportPanel from './components/ExportPanel';
import TimelineValidation from './components/TimelineValidation';

export default function ComplianceDocumentRepository() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('library');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    expiryRange: 'all',
    regulatoryBody: 'all'
  });

  // Set export tab as active when navigating from export route
  useEffect(() => {
    if (location?.pathname === '/compliance-document-export') {
      setActiveTab('export');
    }
  }, [location?.pathname]);

  const tabs = [
    { id: 'library', label: 'Document Library', icon: FileText },
    { id: 'evidence', label: 'Evidence Collection', icon: Upload },
    { id: 'renewal', label: 'Renewal Tracking', icon: Clock },
    { id: 'timeline', label: 'Timeline & Validation', icon: CheckCircle },
    { id: 'audit', label: 'Audit Trail', icon: AlertCircle },
    { id: 'export', label: 'Export', icon: Download }
  ];

  const summaryStats = [
    { label: 'Total Documents', value: '1,247', change: '+8.2%', trend: 'up', icon: FileText },
    { label: 'Expiring Soon', value: '23', change: '5 critical', trend: 'warning', icon: Clock },
    { label: 'Pending Upload', value: '12', change: '-3 from last week', trend: 'down', icon: Upload },
    { label: 'Audit Ready', value: '98.5%', change: '+2.1%', trend: 'up', icon: CheckCircle }
  ];

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setShowViewer(true);
  };

  // Add this block - Mock data for components
  const mockDocuments = [
    { id: 1, name: 'Sample Document', category: 'environmental', status: 'active' }
  ];

  const mockActivities = [
    { id: 1, action: 'Document uploaded', timestamp: new Date() }
  ];

  const handleExport = (format) => {
    console.log('Exporting as:', format);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'library':
        return (
          <DocumentLibrary
            searchQuery={searchQuery}
            filters={filters}
            onDocumentClick={handleDocumentClick}
            documents={mockDocuments}
            title="Document Library"
          />
        );
      case 'evidence':
        return <EvidenceCollection />;
      case 'renewal':
        return <RenewalTracking />;
      case 'timeline':
        return <TimelineValidation />;
      case 'audit':
        return <AuditTrail activities={mockActivities} />;
      case 'export':
        return <ExportPanel onExport={handleExport} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Compliance Document Repository
            </h1>
            <p className="text-gray-600 mt-1">
              Centralized management of regulatory documentation and compliance artifacts
            </p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Filter size={20} />
              <span className="hidden md:inline">Advanced Filters</span>
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Upload size={20} />
              <span className="hidden md:inline">Upload Document</span>
            </button>
          </div>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryStats?.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">{stat?.label}</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">{stat?.value}</p>
                <div className="flex items-center gap-1">
                  <span className={`text-xs font-medium ${
                    stat?.trend === 'up' ? 'text-green-600' : 
                    stat?.trend === 'warning'? 'text-orange-600' : 'text-red-600'
                  }`}>
                    {stat?.change}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${
                stat?.trend === 'up' ? 'bg-green-100 text-green-600' : 
                stat?.trend === 'warning'? 'bg-orange-100 text-orange-600' : 'bg-red-100 text-red-600'
              }`}>
                <stat.icon size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search documents by regulation, category, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filters?.category}
              onChange={(e) => setFilters({ ...filters, category: e?.target?.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="environmental">Environmental Permits</option>
              <option value="safety">Safety Certifications</option>
              <option value="operational">Operational Licenses</option>
              <option value="quality">Quality Standards</option>
            </select>
            <select
              value={filters?.status}
              onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring Soon</option>
              <option value="expired">Expired</option>
              <option value="pending">Pending Submission</option>
            </select>
            <select
              value={filters?.expiryRange}
              onChange={(e) => setFilters({ ...filters, expiryRange: e?.target?.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Time</option>
              <option value="30">Next 30 Days</option>
              <option value="60">Next 60 Days</option>
              <option value="90">Next 90 Days</option>
            </select>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow border border-gray-200 mb-6">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 ${
                  activeTab === tab?.id
                    ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <tab.icon size={20} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
      {/* Document Viewer Modal */}
      {showViewer && selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          onClose={() => {
            setShowViewer(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
}